import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Avatar,
    Typography,
    ButtonBase,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Tooltip,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { LOGOUT_URL } from '@src/global/login';

const FooterContainer = styled(Box)(({ theme }) => ({
    flexShrink: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: '8px 10px',
    backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.02) : '#FFFFFF',
}));

const AccountButton = styled(ButtonBase, {
    shouldForwardProp: (prop) => prop !== 'sidebarOpen',
})(({ theme, sidebarOpen }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: sidebarOpen ? '6px 8px' : '6px 0',
    borderRadius: '7px',
    textAlign: 'left',
    justifyContent: sidebarOpen ? 'flex-start' : 'center',
    gap: sidebarOpen ? '10px' : 0,
    transition: 'background-color 0.15s ease',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
    width: 32,
    height: 32,
    fontSize: '0.8125rem',
    fontWeight: 600,
    backgroundColor: alpha(theme.palette.primary.main, 0.14),
    color: theme.palette.primary.main,
    borderRadius: '50%',
}));

const getUserProfile = () => {
    let userInfo = null;
    try {
        const stored = window.localStorage.getItem('user_info');
        if (stored) {
            userInfo = JSON.parse(stored);
        }
    } catch (_e) {
        userInfo = null;
    }

    const firstName = userInfo?.first_name || '';
    const lastName = userInfo?.last_name || '';
    const fullName = (firstName || lastName)
        ? `${firstName} ${lastName}`.trim()
        : userInfo?.name || 'Administrator';

    const initials = (firstName && lastName)
        ? `${firstName[0]}${lastName[0]}`.toUpperCase()
        : (fullName ? fullName.slice(0, 2).toUpperCase() : 'AD');

    const roleName = userInfo?.producer_role ||
        (userInfo?.access_level ? userInfo.access_level.charAt(0).toUpperCase() + userInfo.access_level.slice(1) : 'Administrator');

    return { userInfo, fullName, initials, roleName };
};

const NavAccountFooter = ({ sidebarOpen }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

    const { userInfo, fullName, initials, roleName } = getUserProfile();

    const handleOpenMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleOpenLogout = () => {
        handleCloseMenu();
        setLogoutDialogOpen(true);
    };

    const handleCloseLogout = () => {
        setLogoutDialogOpen(false);
    };

    const handleConfirmLogout = () => {
        window.localStorage.removeItem('jwt');
        window.localStorage.removeItem('user_info');
        setLogoutDialogOpen(false);
        window.location.replace(LOGOUT_URL);
    };

    const buttonContent = (
        <AccountButton
            aria-haspopup="true"
            aria-label="User account menu"
            sidebarOpen={sidebarOpen}
            onClick={handleOpenMenu}
        >
            <UserAvatar>{initials}</UserAvatar>
            {sidebarOpen ? (
                <>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            noWrap
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                fontSize: '0.8125rem',
                                color: 'text.primary',
                            }}
                        >
                            {fullName}
                        </Typography>
                        <Typography
                            noWrap
                            variant="caption"
                            sx={{
                                display: 'block',
                                fontSize: '0.725rem',
                                color: 'text.secondary',
                            }}
                        >
                            {roleName}
                        </Typography>
                    </Box>
                    <UnfoldMoreIcon sx={{ fontSize: '1.15rem', color: 'text.secondary', flexShrink: 0 }} />
                </>
            ) : null}
        </AccountButton>
    );

    return (
        <FooterContainer>
            {sidebarOpen ? (
                buttonContent
            ) : (
                <Tooltip placement="right" title={`${fullName} (${roleName})`}>
                    {buttonContent}
                </Tooltip>
            )}

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: sidebarOpen ? 'center' : 'right',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: sidebarOpen ? 'center' : 'left',
                }}
                PaperProps={{
                    sx: {
                        width: 230,
                        borderRadius: '10px',
                        boxShadow: (theme) => theme.shadows[4],
                        mt: -1,
                        '& .MuiMenuItem-root': {
                            fontSize: '14px',
                        },
                        '& .MuiListItemText-primary': {
                            fontSize: '14px',
                        },
                    },
                }}
                onClose={handleCloseMenu}
            >
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography noWrap variant="subtitle2" sx={{ fontWeight: 600, fontSize: '14px' }}>
                        {fullName}
                    </Typography>
                    <Typography noWrap variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {userInfo?.email || roleName}
                    </Typography>
                </Box>
                <Divider />
                <MenuItem disabled sx={{ py: 1 }}>
                    <ListItemIcon>
                        <PersonOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="Role"
                        primaryTypographyProps={{ fontSize: '14px' }}
                        secondary={roleName}
                        secondaryTypographyProps={{ fontSize: '12.5px' }}
                    />
                </MenuItem>
                <Divider />
                <MenuItem sx={{ color: 'error.main', py: 1 }} onClick={handleOpenLogout}>
                    <ListItemIcon sx={{ color: 'error.main' }}>
                        <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary="Sign Out"
                        primaryTypographyProps={{ fontSize: '14px' }}
                    />
                </MenuItem>
            </Menu>

            <Dialog
                open={logoutDialogOpen}
                aria-labelledby="logout-dialog-title"
                aria-describedby="logout-dialog-description"
                onClose={handleCloseLogout}
            >
                <DialogTitle id="logout-dialog-title">Log out</DialogTitle>
                <DialogContent>
                    <DialogContentText id="logout-dialog-description">
                        Are you sure you want to log out of Producer News Portal?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button color="inherit" onClick={handleCloseLogout}>
                        Cancel
                    </Button>
                    <Button autoFocus color="primary" variant="contained" onClick={handleConfirmLogout}>
                        Log Out
                    </Button>
                </DialogActions>
            </Dialog>
        </FooterContainer>
    );
};

NavAccountFooter.propTypes = {
    sidebarOpen: PropTypes.bool,
};

export default NavAccountFooter;
